import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Paper, 
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Divider,
    useTheme,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TablePagination,
    Menu,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Avatar,
    Badge,
    Tooltip,
    Stack,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Alert,
    AlertTitle,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { 
    Delete, 
    Receipt, 
    PictureAsPdf,
    TableChart, 
    GetApp,
    Phone as PhoneIcon,
    ShoppingCart,
    Person,
    Add as AddIcon,
    Payment,
    Visibility,
    Print,
    CheckCircle,
    Cancel,
    HourglassEmpty,
    Description,
    ExpandMore,
    AccessTime,
    Notes,
    LocalBar,
    AttachMoney
} from '@mui/icons-material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import { 
    APIClubOrderList,
    APIEmployeeNames,
    APIClubOrderDelete,
    APIClubOrderPayment,
    APIClubOrderUpdate
} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import { 
    formatCreatedAtDate, 
    formatNumberWithComma, 
    formatNumberWithComma2, 
    getCategoriesNameById, 
    getFoodTypeById, 
    getUserDetails, 
    recordsPerPage
} from '../../../utils/helpers';
import { 
    exportOrdersToCSV, 
    exportOrdersToExcel, 
    exportOrdersToPDFEnhanced 
} from '../../../services/club/OrderListService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { foodTypes, paymentModes } from '../../../utils/dataInfo';
import PrintOrderReceipt from './PrintOrderReceipt';
import { apiService } from '../../../apiServices/ApiService';
import { useNavigate } from 'react-router-dom';

const ClubOrderList = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('UPDATE');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [printReceiptData, setPrintReceiptData] = useState(null);
    const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
    const [expandedOrder, setExpandedOrder] = useState(null);

    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Form states
    const [formData, setFormData] = useState({
        order_status: '',
        payment_method: '',
        order_notes: ''
    });

    const [paymentForm, setPaymentForm] = useState({
        payment_method: '',
        amount_paid: '',
        transaction_id: '',
        payment_notes: ''
    });

    const [filters, setFilters] = useState({
        order_number: '',
        order_status: '',
        payment_method: '',
        dateFrom: '',
        counter: '',
        food_type_id: '',
        dateTo: ''
    });

    const [errors, setErrors] = useState({
        order_status: false,
        payment_method: false
    });

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openStatusUpdate, setOpenStatusUpdate] = useState(false);
    const [openPayment, setOpenPayment] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState('');
    const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);
    const [referenceData, setReferenceData] = useState({
        branchNames: [],
        customerType: [],
        counterNames: [],
        employeeNames: [],
    });

    useEffect(() => {
        apiService.setToastFunction(showToast);
    }, [showToast]);

    // Permissions
    const [userRole, setUserRole] = useState('');
    useEffect(() => {
        const role = getUserRole();
        setUserRole(role);
    }, []);

    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    useEffect(() => {
        fetchData();
        fetchReferenceData();
    }, []);

    const fetchReferenceData = async () => {
        try {
            // Fetch all reference data in parallel
            const [branchesResult, empResult] = await Promise.all([
                apiService.branchNames(),
                apiService.listNames(APIEmployeeNames),
            ]);

            setReferenceData({
                branchNames: branchesResult.success ? branchesResult.data : [],
                employeeNames: empResult.success ? empResult.data : [],
            });
        } catch (error) {
            showToast("Error", "Failed to load reference data", "error");
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {

            const params = {};
			let roleToUse = getUserDetails("roleId");
			if (roleToUse === "ST") {
				params.counter_id = getUserDetails("counterId");
			}
            // Apply filters if any
            if (filters.order_status) {
                params.order_status = filters.order_status;
            }
            if (filters.payment_method) {
                params.payment_method = filters.payment_method;
            }
            if (filters.counter) {
                params.counter = filters.counter;
            }
            if (filters.food_type_id) {
                params.food_type_id = filters.food_type_id;
            }
            if (filters.dateFrom) {
                params.date_from = filters.dateFrom;
            }
            if (filters.dateTo) {
                params.date_to = filters.dateTo;
            }

            const response = await axios.get(APIClubOrderList, { params });
            
            // Parse the JSON items for each order
            const orders = Array.isArray(response.data.data) 
                ? response.data.data.map(order => ({
                    ...order,
                    // Ensure club_order_items is parsed as an array
                    club_order_items: typeof order.club_order_items === 'string' 
                        ? JSON.parse(order.club_order_items) 
                        : order.club_order_items || []
                  })).reverse() 
                : [];

            setPageState(prev => ({
                ...prev,
                pageData: orders,
            }));

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            console.log(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAddNewOrder = (order) => {
        navigate('/club/pos/pos', {
            state: {
                customerId: order.customer_id,
                customerTypeId: order.customer_type_id,
                previousOrderNumber: order.order_number,
                counterId: order.counter_id
            }
        });
    };

    const handleBranchSelect = (branchId) => {
        setSelectedBranch(branchId);
        setBranchSelectionOpen(false);
        fetchData(branchId);
    };

    // Helper function to get status color
    const getStatusColor = (status) => {
        switch(status) {
            case 'completed': return 'success';
            case 'pending': return 'warning';
            case 'cancelled': return 'error';
            case 'quotation': return 'info';
            default: return 'default';
        }
    };

    // Helper function to get payment method color
    const getPaymentMethodColor = (method) => {
        switch(method) {
            case 'cash': return 'success';
            case 'card': return 'info';
            case 'bank': return 'primary';
            case 'mobile': return 'secondary';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    // Helper function to get status icon
    const getStatusIcon = (status) => {
        switch(status) {
            case 'completed': return <CheckCircle />;
            case 'pending': return <HourglassEmpty />;
            case 'cancelled': return <Cancel />;
            case 'quotation': return <Description />;
            default: return null;
        }
    };

    // Get unique servants from order items
    const getUniqueServants = (order) => {
        if (!order.club_order_items || !Array.isArray(order.club_order_items)) return [];
        
        const servantIds = [...new Set(order.club_order_items.map(item => item.servant_id))];
        return servantIds.map(id => ({
            id,
            name: getCategoriesNameById(id, referenceData.employeeNames) || 'Unknown'
        }));
    };

    // Calculate item totals by servant
    const getItemsByServant = (order) => {
        if (!order.club_order_items || !Array.isArray(order.club_order_items)) return {};
        
        return order.club_order_items.reduce((acc, item) => {
            const servantId = item.servant_id;
            if (!acc[servantId]) {
                acc[servantId] = {
                    servantName: getCategoriesNameById(servantId, referenceData.employeeNames) || 'Unknown',
                    items: [],
                    total: 0
                };
            }
            acc[servantId].items.push(item);
            acc[servantId].total += item.total_amount || (item.price * item.quantity);
            return acc;
        }, {});
    };

    // Filter orders based on search term and filters
    const filteredData = pageState.pageData.filter(order => {
        const matchesSearch = searchTerm === '' ||
            order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.order_notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (order.club_order_items?.some(item => 
                item.stock_name?.toLowerCase().includes(searchTerm.toLowerCase())
            ));

        const matchesOrderNumber = !filters.order_number || 
            order.order_number.includes(filters.order_number);

        const matchesOrderStatus = !filters.order_status || 
            order.order_status === filters.order_status;

        const matchesPaymentMethod = !filters.payment_method || 
            order.payment_method === filters.payment_method;

        const matchesCounter = !filters.counter || 
            order.counter?.id === filters.counter;

        const matchesFoodType = !filters.food_type_id || 
            order.club_order_items?.some(item => item.food_type_id === filters.food_type_id);

        let matchesDate = true;
        if (filters.dateFrom) {
            const recordDate = new Date(order.created_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate >= filterFromDate;
        }

        if (filters.dateTo) {
            const recordDate = new Date(order.created_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && recordDate <= filterToDate;
        }

        return matchesSearch && matchesOrderNumber && matchesOrderStatus && 
                matchesPaymentMethod && matchesCounter && matchesFoodType && matchesDate;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenView = (order) => {
        setSelectedRecord(order);
        setOpenView(true);
    };

    const handleCloseView = () => setOpenView(false);

    const handleOpenStatusUpdate = (order) => {
        setSelectedRecord(order);
        setUpdateId(order.id);
        setFormData({
            order_status: order.order_status,
            payment_method: order.payment_method,
            order_notes: order.order_notes || ''
        });
        setOpenStatusUpdate(true);
    };

    const handleCloseStatusUpdate = () => {
        setOpenStatusUpdate(false);
    };

    const handleOpenPayment = (order) => {
        setSelectedRecord(order);
        setUpdateId(order.id);
        setPaymentForm({
            payment_method: order.payment_method === 'pending' ? 'cash' : order.payment_method,
            amount_paid: order.total_amount,
            transaction_id: '',
            payment_notes: ''
        });
        setOpenPayment(true);
    };

    const handleClosePayment = () => {
        setOpenPayment(false);
    };

    const handleOpenDelete = (order) => {
        setSelectedRecord(order);
        setUpdateId(order.id);
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePaymentInputChange = (e) => {
        const { name, value } = e.target;
        setPaymentForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
        fetchData(selectedBranch);
    };

    const clearFilters = () => {
        setFilters({
            order_number: '',
            order_status: '',
            payment_method: '',
            counter: '',
            food_type_id: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
        fetchData(selectedBranch);
    };

    const applyFilters = () => {
        handleCloseOptions();
        fetchData(selectedBranch);
    };

    const validateStatusForm = () => {
        if (!formData.order_status) {
            showToast("Validation Error", "Order status is required", "warning");
            return false;
        }
        return true;
    };

    const validatePaymentForm = () => {
        if (!paymentForm.payment_method) {
            showToast("Validation Error", "Payment method is required", "warning");
            return false;
        }
        if (!paymentForm.amount_paid || parseFloat(paymentForm.amount_paid) <= 0) {
            showToast("Validation Error", "Valid amount is required", "warning");
            return false;
        }
        if (parseFloat(paymentForm.amount_paid) > parseFloat(selectedRecord?.total_amount || 0)) {
            showToast("Validation Error", "Amount paid cannot exceed total amount", "warning");
            return false;
        }
        // Make transaction ID required for non-cash payments
        if (paymentForm.payment_method !== 'cash' && !paymentForm.transaction_id.trim()) {
            showToast("Validation Error", "Transaction ID is required for non-cash payments", "warning");
            return false;
        }
        return true;
    };

    // Export handlers
    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExportExcel = async () => {
        try {
            handleExportClose();
            await exportOrdersToExcel(filteredData);
            showToast("Success", "Excel file downloaded successfully", "success");
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportPDF = async () => {
        try {
            handleExportClose();
            await exportOrdersToPDFEnhanced(filteredData, referenceData.employeeNames, referenceData.branchNames, filters);
            showToast("Success", "PDF file downloaded successfully", "success");
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const handleExportCSV = async () => {
        try {
            handleExportClose();
            await exportOrdersToCSV(filteredData);
            showToast("Success", "CSV file downloaded successfully", "success");
        } catch (error) {
            console.error('CSV export error:', error);
            showToast("Error", error.message, "error");
        }
    };

    const getSelectedBranchName = () => {
        if (!selectedBranch) return "Select Branch";
        const branch = referenceData.branchNames.find(b => b.id === selectedBranch);
        return branch ? `${branch.name} (${branch.code})` : "Select Branch";
    };

    // Handle status update
    const handleStatusUpdate = async (e) => {
        e.preventDefault();
        if (!validateStatusForm()) return;

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('order_id', updateId);
            formDataToSend.append('order_status', formData.order_status);
            formDataToSend.append('order_notes', formData.order_notes);
            formDataToSend.append('branch_id', selectedBranch || getUserDetails("branchId"));

            const response = await axios.post(APIClubOrderUpdate, formDataToSend);

            if (response.data.status === "success") {
                showToast("Success", "Order status updated successfully", "success");
                handleCloseStatusUpdate();
                fetchData();
            } else {
                showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
            }
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;
                if (status === 422) {
                    const errors = data.errors || {};
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(`${field}: ${errors[field].join(', ')}`);
                    }
                    showToast(
                        "Validation Error",
                        errorMessages.join('\n') || "Please correct the form errors",
                        "error"
                    );
                } else if (status === 400) {
                    showToast("Error", data.message || "Invalid request", "error");
                } else {
                    showToast("Error", data.message || "Request failed", "error");
                }
            } else {
                showToast("Error", error.message || "An unexpected error occurred", "error");
            }
            console.error("API Error:", error);
        }
    };

    // Handle payment
    const handlePayment = async (e) => {
        e.preventDefault();
        if (!validatePaymentForm()) return;

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('order_id', updateId);
            formDataToSend.append('payment_method', paymentForm.payment_method);
            formDataToSend.append('amount_paid', paymentForm.amount_paid);
            formDataToSend.append('transaction_id', paymentForm.transaction_id);
            formDataToSend.append('payment_notes', paymentForm.payment_notes);
            formDataToSend.append('branch_id', selectedBranch || getUserDetails("branchId"));

            const response = await axios.post(APIClubOrderPayment, formDataToSend);

            if (response.data.status === "success") {
                showToast("Success", "Payment recorded successfully", "success");
                handleClosePayment();
                fetchData();
            } else {
                showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
            }
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;
                if (status === 422) {
                    const errors = data.errors || {};
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(`${field}: ${errors[field].join(', ')}`);
                    }
                    showToast(
                        "Validation Error",
                        errorMessages.join('\n') || "Please correct the form errors",
                        "error"
                    );
                } else if (status === 400) {
                    showToast("Error", data.message || "Invalid payment request", "error");
                } else {
                    showToast("Error", data.message || "Request failed", "error");
                }
            } else {
                showToast("Error", error.message || "An unexpected error occurred", "error");
            }
            console.error("API Error:", error);
        }
    };

    // Handle delete
    const handleDelete = async () => {
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('order_id', updateId);
            formDataToSend.append('branch_id', selectedBranch || getUserDetails("branchId"));

            const response = await axios.post(APIClubOrderDelete, formDataToSend);

            if (response.data.status === "success") {
                showToast("Success", "Order deleted successfully", "success");
                handleCloseDelete();
                fetchData();
            } else {
                showToast("Warning", response.data.message || "Operation completed with warnings", "warning");
            }
        } catch (error) {
            if (error.response) {
                const { status, data } = error.response;
                if (status === 422) {
                    const errors = data.errors || {};
                    let errorMessages = [];
                    for (const field in errors) {
                        errorMessages.push(`${field}: ${errors[field].join(', ')}`);
                    }
                    showToast(
                        "Validation Error",
                        errorMessages.join('\n') || "Please correct the form errors",
                        "error"
                    );
                } else if (status === 400) {
                    showToast("Error", data.message || "Invalid delete request", "error");
                } else {
                    showToast("Error", data.message || "Request failed", "error");
                }
            } else {
                showToast("Error", error.message || "An unexpected error occurred", "error");
            }
            console.error("API Error:", error);
        }
    };

    // Handle print receipt
    const handlePrintReceipt = (order) => {
        setPrintReceiptData(order);
        setIsPrintDialogOpen(true);
    };

    const handleClosePrintDialog = () => {
        setIsPrintDialogOpen(false);
        setPrintReceiptData(null);
    };

    // Calculate totals
    const calculateTotals = () => {
        const totals = {
            totalOrders: filteredData.length,
            totalAmount: filteredData.reduce((sum, order) => sum + parseFloat(order.total_amount), 0),
            pendingOrders: filteredData.filter(order => order.order_status === 'pending').length,
            completedOrders: filteredData.filter(order => order.order_status === 'completed').length
        };
        return totals;
    };

    const totals = calculateTotals();

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
                mb: { xs: 1, sm: 0 }
            }}>
                Order Management ( {formatNumberWithComma2(pageState.pageData.length)} Total Orders )
            </Typography>
            
            {/* Display selected branch name */}
            {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
                <Box sx={{ mt: 1, mb: 2 }}>
                    <Chip
                        label={`Selected Branch: ${getSelectedBranchName()}`}
                        color="primary"
                        variant="filled"
                        onDelete={() => {
                            setSelectedBranch('');
                            fetchData();
                        }}
                        deleteIcon={<CloseIcon />}
                    />
                </Box>
            )}

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Total Orders</Typography>
                            <Typography variant="h4">{formatNumberWithComma2(totals.totalOrders)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Total Amount</Typography>
                            <Typography variant="h4">{formatNumberWithComma(totals.totalAmount)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Pending Orders</Typography>
                            <Typography variant="h4">{formatNumberWithComma2(totals.pendingOrders)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Completed Orders</Typography>
                            <Typography variant="h4">{formatNumberWithComma2(totals.completedOrders)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexShrink: 0,
                        order: { xs: 2, sm: 1 }
                    }}>
                        {hasPermission(PERMISSIONS.BRANCHES) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<FilterIcon />}
                                onClick={() => setBranchSelectionOpen(true)}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Select Branch
                            </Button>
                        )}
                        
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<FilterIcon />}
                            onClick={handlefilterOptions}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                        >
                            View Options
                        </Button>
                        
                        {hasPermission(PERMISSIONS.EXPORT) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Export Data
                            </Button>
                        )}
                    </Box>

                    <Paper sx={{
                        width: { xs: '100%', sm: '300px' },
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: { xs: 0, sm: 'auto' },
                        order: { xs: 1, sm: 2 },
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search orders, customers, items..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.order_number || filters.order_status || filters.payment_method || 
                    filters.counter || filters.food_type_id || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.order_number && (
                            <Chip
                                label={`Order #: ${filters.order_number}`}
                                onDelete={() => setFilters(prev => ({ ...prev, order_number: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.order_status && (
                            <Chip
                                label={`Status: ${filters.order_status}`}
                                onDelete={() => setFilters(prev => ({ ...prev, order_status: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.payment_method && (
                            <Chip
                                label={`Payment: ${filters.payment_method}`}
                                onDelete={() => setFilters(prev => ({ ...prev, payment_method: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.counter && (
                            <Chip
                                label={`Counter: ${pageState.pageData.find(o => o.counter?.id === filters.counter)?.counter?.name || filters.counter}`}
                                onDelete={() => setFilters(prev => ({ ...prev, counter: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip 
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip 
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button 
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': {
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.95rem'
                                    }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Order #</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Counter</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Items</TableCell>
                                    <TableCell>Total Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Payment</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((order, index) => {
                                    const items = order.club_order_items || [];
                                    const itemCount = items.length;
                                    const uniqueServants = getUniqueServants(order);
                                    
                                    return (
                                        <React.Fragment key={order.id}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    '&:nth-of-type(odd)': {
                                                        backgroundColor: theme.palette.action.hover
                                                    },
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                                            >
                                                <TableCell>
                                                    <Chip
                                                        label={page * rowsPerPage + index + 1}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>
                                                    {order.order_number}
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {formatCreatedAtDate(order.created_at)}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {new Date(order.created_at).toLocaleTimeString()}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={order.counter?.name || 'N/A'}
                                                        color="info"
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {order.customer_id}
                                                        </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge 
                                                        badgeContent={itemCount} 
                                                        color="primary"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <ShoppingCart />
                                                    </Badge>
                                                    <Typography variant="body2" component="span">
                                                        {itemCount} {itemCount === 1 ? 'item' : 'items'}
                                                    </Typography>
                                                    {uniqueServants.length > 0 && (
                                                        <Tooltip title={`Servants: ${uniqueServants.map(s => s.name).join(', ')}`}>
                                                            <Chip
                                                                size="small"
                                                                label={`${uniqueServants.length} servant${uniqueServants.length > 1 ? 's' : ''}`}
                                                                sx={{ ml: 1, fontSize: '0.7rem' }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                    {formatNumberWithComma(order.total_amount)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={getStatusIcon(order.order_status)}
                                                        label={order.order_status.toUpperCase()}
                                                        color={getStatusColor(order.order_status)}
                                                        size="small"
                                                        variant="filled"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={order.payment_method.toUpperCase()}
                                                        color={getPaymentMethodColor(order.payment_method)}
                                                        size="small"
                                                        variant={order.payment_method === 'pending' ? 'outlined' : 'filled'}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Stack direction="row" spacing={1} justifyContent="center">
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenView(order);
                                                                }}
                                                                color="primary"
                                                            >
                                                                <Visibility />
                                                            </IconButton>
                                                        </Tooltip>



                                                        <Tooltip title="Print Receipt">
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handlePrintReceipt(order);
                                                                }}
                                                                color="secondary"
                                                            >
                                                                <Print />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {order.order_status === 'pending' && (
                                                            <>
																{hasPermission(PERMISSIONS.APEND) &&(<>
																	<Tooltip title="Add New Order (Same Customer)">
																		<IconButton
																			size="small"
																			onClick={(e) => {
																				e.stopPropagation();
																				handleAddNewOrder(order);
																			}}
																			color="success"
																		>
																			<AddIcon />
																		</IconButton>
																	</Tooltip>
																</>)}
                                                                <Tooltip title="Make Payment">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleOpenPayment(order);
                                                                        }}
                                                                        color="success"
                                                                    >
                                                                        <Payment />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                
                                                                <Tooltip title="Update Status">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleOpenStatusUpdate(order);
                                                                        }}
                                                                        color="warning"
                                                                    >
                                                                        <EditIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                        
                                                        {hasPermission(PERMISSIONS.DELETE) && order.order_status === 'pending' && (
                                                            <Tooltip title="Delete Order">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleOpenDelete(order);
                                                                    }}
                                                                    color="error"
                                                                >
                                                                    <Delete />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                            
                                            {/* Expanded row showing order items */}
                                            {expandedOrder === order.id && (
                                                <TableRow>
                                                    <TableCell colSpan={10} sx={{ p: 0, borderBottom: 'none' }}>
                                                        <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
                                                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                                                <ShoppingCart sx={{ mr: 1 }} /> Order Items
                                                                {order.order_notes && (
                                                                    <Chip
                                                                        size="small"
                                                                        icon={<Notes />}
                                                                        label="Has Notes"
                                                                        sx={{ ml: 2 }}
                                                                        color="info"
                                                                    />
                                                                )}
                                                            </Typography>
                                                            
                                                            {/* Group items by servant */}
                                                            {Object.entries(getItemsByServant(order)).map(([servantId, servantData]) => (
                                                                <Accordion key={servantId} defaultExpanded sx={{ mb: 2 }}>
                                                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                                            <Person sx={{ mr: 1, color: 'primary.main' }} />
                                                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                                                {servantData.servantName}
                                                                            </Typography>
                                                                            <Chip
                                                                                size="small"
                                                                                label={`Total: ${formatNumberWithComma(servantData.total)}`}
                                                                                color="primary"
                                                                                sx={{ ml: 2 }}
                                                                            />
                                                                            <Chip
                                                                                size="small"
                                                                                label={`${servantData.items.length} items`}
                                                                                variant="outlined"
                                                                                sx={{ ml: 1 }}
                                                                            />
                                                                        </Box>
                                                                    </AccordionSummary>
                                                                    <AccordionDetails>
                                                                        <TableContainer component={Paper} variant="outlined">
                                                                            <Table size="small">
                                                                                <TableHead>
                                                                                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                                                                                        <TableCell sx={{ color: 'white' }}>Time</TableCell>
                                                                                        <TableCell sx={{ color: 'white' }}>Item</TableCell>
                                                                                        <TableCell align="right" sx={{ color: 'white' }}>Qty</TableCell>
                                                                                        <TableCell align="right" sx={{ color: 'white' }}>Price</TableCell>
                                                                                        <TableCell align="right" sx={{ color: 'white' }}>Total</TableCell>
                                                                                        {order.order_notes && <TableCell sx={{ color: 'white' }}>Notes</TableCell>}
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {servantData.items.map((item, idx) => (
                                                                                        <TableRow key={idx}>
                                                                                            <TableCell>
                                                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                                                    <AccessTime fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                                                                                    <Typography variant="body2">
                                                                                                        {new Date(item.time).toLocaleTimeString()}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            </TableCell>
                                                                                            <TableCell>
                                                                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                                                    {item.stock_name}
                                                                                                </Typography>
                                                                                            </TableCell>
                                                                                            <TableCell align="right">{item.quantity}</TableCell>
                                                                                            <TableCell align="right">{formatNumberWithComma(item.price)}</TableCell>
                                                                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                                                                {formatNumberWithComma(item.total_amount)}
                                                                                            </TableCell>
                                                                                            {order.order_notes && (
                                                                                                <TableCell>
                                                                                                    {item.order_notes && (
                                                                                                        <Tooltip title={item.order_notes}>
                                                                                                            <Notes fontSize="small" color="action" />
                                                                                                        </Tooltip>
                                                                                                    )}
                                                                                                </TableCell>
                                                                                            )}
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </TableContainer>
                                                                    </AccordionDetails>
                                                                </Accordion>
                                                            ))}
                                                            
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                justifyContent: 'flex-end', 
                                                                mt: 2,
                                                                p: 2,
                                                                bgcolor: 'primary.light',
                                                                borderRadius: 1
                                                            }}>
                                                                <Typography variant="h6" sx={{ color: 'white' }}>
                                                                    Grand Total: {formatNumberWithComma(order.total_amount)}
                                                                </Typography>
                                                            </Box>
                                                            
                                                            {order.order_notes && (
                                                                <Alert severity="info" sx={{ mt: 2 }}>
                                                                    <AlertTitle>Order Notes</AlertTitle>
                                                                    {order.order_notes}
                                                                </Alert>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ mt: 2 }}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 8,
                            color: 'text.secondary'
                        }}>
                            <Receipt sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No orders found</Typography>
                            <Typography variant="body2">Try adjusting your search query or filters</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Branch Selection Dialog */}
            <Dialog
                open={branchSelectionOpen}
                onClose={() => setBranchSelectionOpen(false)}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel id="branch-select-label">Select Branch</InputLabel>
                            <Select
                                labelId="branch-select-label"
                                value={selectedBranch}
                                label="Select Branch"
                                onChange={(e) => handleBranchSelect(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Select a branch</em>
                                </MenuItem>
                                {referenceData.branchNames.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name} ({branch.code})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => setBranchSelectionOpen(false)}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (selectedBranch) {
                                fetchData(selectedBranch);
                            }
                            setBranchSelectionOpen(false);
                        }}
                        color="primary"
                        variant="contained"
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
                <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="info" />
                    Export to CSV (.csv)
                </MenuItem>
            </Menu>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                onClose={handleCloseOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Orders
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Orders
                        </Button>

                        <Divider>OR</Divider>

                        <TextField
                            fullWidth
                            label="Order Number"
                            name="order_number"
                            value={filters.order_number}
                            onChange={handleFilterChange}
                            placeholder="e.g., ORD-20251219-0001"
                        />

                        <FormControl fullWidth>
                            <InputLabel>Order Status</InputLabel>
                            <Select
                                name="order_status"
                                value={filters.order_status}
                                onChange={handleFilterChange}
                                label="Order Status"
                            >
                                <MenuItem value="">
                                    <em>All Status</em>
                                </MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                                <MenuItem value="quotation">Quotation</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Payment Method</InputLabel>
                            <Select
                                name="payment_method"
                                value={filters.payment_method}
                                onChange={handleFilterChange}
                                label="Payment Method"
                            >
                                <MenuItem value="">
                                    <em>All Methods</em>
                                </MenuItem>
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="cash">Cash</MenuItem>
                                <MenuItem value="card">Card</MenuItem>
                                <MenuItem value="bank">Bank</MenuItem>
                                <MenuItem value="mobile">Mobile Money</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Add Counter Filter */}
                        <FormControl fullWidth>
                            <InputLabel>Counter</InputLabel>
                            <Select
                                name="counter"
                                value={filters.counter}
                                onChange={handleFilterChange}
                                label="Counter"
                            >
                                <MenuItem value="">
                                    <em>All Counters</em>
                                </MenuItem>
                                {pageState.pageData
                                    .filter((order, index, self) =>
                                        order.counter?.id &&
                                        self.findIndex(o => o.counter?.id === order.counter.id) === index
                                    )
                                    .map((order) => (
                                        <MenuItem key={order.counter?.id} value={order.counter?.id}>
                                            {order.counter?.name || 'Unknown'}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseOptions}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={applyFilters}
                        color="primary"
                        variant="contained"
                    >
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Order Details Dialog */}
            <Dialog
                open={openView}
                onClose={handleCloseView}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="md"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Order Details - {selectedRecord?.order_number}</b>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            {/* Order Header */}
                            <Grid container spacing={3} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Order Number</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        {selectedRecord.order_number}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Order Date & Time</Typography>
                                    <Typography variant="body1">
                                        {formatCreatedAtDate(selectedRecord.created_at)} at {new Date(selectedRecord.created_at).toLocaleTimeString()}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                    <Chip
                                        icon={getStatusIcon(selectedRecord.order_status)}
                                        label={selectedRecord.order_status.toUpperCase()}
                                        color={getStatusColor(selectedRecord.order_status)}
                                        size="medium"
                                        sx={{ mt: 0.5 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Payment Method</Typography>
                                    <Chip
                                        label={selectedRecord.payment_method.toUpperCase()}
                                        color={getPaymentMethodColor(selectedRecord.payment_method)}
                                        size="medium"
                                        sx={{ mt: 0.5 }}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            {/* Customer Information */}
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                <Person sx={{ mr: 1 }} /> Customer Information
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Customer Name</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {selectedRecord.customer_id}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Customer Type</Typography>
                                    <Typography variant="body1">
                                        {selectedRecord.customer_type?.name || 'N/A'}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            {/* Staff Information */}
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                <Person sx={{ mr: 1 }} /> Staff Information
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Counter</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {selectedRecord.counter?.name || 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Servants</Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                        {getUniqueServants(selectedRecord).map(servant => (
                                            <Chip
                                                key={servant.id}
                                                size="small"
                                                label={servant.name}
                                                icon={<Person />}
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />
                            
                            {/* Payment Information */}
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                <AttachMoney sx={{ mr: 1 }} /> Payment Information
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Payment Method</Typography>
                                    <Chip
                                        label={selectedRecord.payment_method.toUpperCase()}
                                        color={getPaymentMethodColor(selectedRecord.payment_method)}
                                        size="medium"
                                        sx={{ mt: 0.5 }}
                                    />
                                </Grid>
                                {selectedRecord.transaction_id && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Transaction ID</Typography>
                                        <Typography variant="body1" sx={{ 
                                            fontFamily: 'monospace',
                                            backgroundColor: 'action.hover',
                                            p: 1,
                                            borderRadius: 1
                                        }}>
                                            {selectedRecord.transaction_id}
                                        </Typography>
                                    </Grid>
                                )}
                                {selectedRecord.payment_notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">Payment Notes</Typography>
                                        <Paper sx={{ 
                                            p: 2, 
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                            mt: 0.5
                                        }}>
                                            <Typography variant="body1">
                                                {selectedRecord.payment_notes}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>

                            <Divider sx={{ my: 2 }} />
                            
                            {/* Order Items */}
                            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                <ShoppingCart sx={{ mr: 1 }} /> Order Items ({selectedRecord.club_order_items?.length || 0})
                            </Typography>
                            
                            {Object.entries(getItemsByServant(selectedRecord)).map(([servantId, servantData]) => (
                                <Accordion key={servantId} defaultExpanded sx={{ mb: 2 }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <Person sx={{ mr: 1, color: 'primary.main' }} />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {servantData.servantName}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={`Total: ${formatNumberWithComma(servantData.total)}`}
                                                color="primary"
                                                sx={{ ml: 2 }}
                                            />
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                                                        <TableCell sx={{ color: 'white' }}>Time</TableCell>
                                                        <TableCell sx={{ color: 'white' }}>Item</TableCell>
                                                        <TableCell align="right" sx={{ color: 'white' }}>Qty</TableCell>
                                                        <TableCell align="right" sx={{ color: 'white' }}>Price</TableCell>
                                                        <TableCell align="right" sx={{ color: 'white' }}>Total</TableCell>
                                                        <TableCell sx={{ color: 'white' }}>Notes</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {servantData.items.map((item, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <AccessTime fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                                                    <Typography variant="body2">
                                                                        {new Date(item.time).toLocaleTimeString()}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                    {item.stock_name}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">{item.quantity}</TableCell>
                                                            <TableCell align="right">{formatNumberWithComma(item.price)}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                                                {formatNumberWithComma(item.total_amount)}
                                                            </TableCell>
                                                            <TableCell>
                                                                {item.order_notes && (
                                                                    <Tooltip title={item.order_notes}>
                                                                        <Notes fontSize="small" color="action" />
                                                                    </Tooltip>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                            
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                mt: 2,
                                p: 2,
                                bgcolor: 'primary.light',
                                borderRadius: 1
                            }}>
                                <Typography variant="h6" sx={{ color: 'white' }}>
                                    Grand Total: {formatNumberWithComma(selectedRecord.total_amount)}
                                </Typography>
                            </Box>

                            {/* Order Notes */}
                            {selectedRecord.order_notes && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Alert severity="info">
                                        <AlertTitle>Order Notes</AlertTitle>
                                        {selectedRecord.order_notes}
                                    </Alert>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseView}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={() => {
                            handleCloseView();
                            handlePrintReceipt(selectedRecord);
                        }}
                        color="primary"
                        variant="contained"
                        startIcon={<Print />}
                    >
                        Print Receipt
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Update Status Dialog */}
            <Dialog
                open={openStatusUpdate}
                onClose={handleCloseStatusUpdate}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Update Order Status - {selectedRecord?.order_number}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleStatusUpdate} sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Order Status</InputLabel>
                                    <Select
                                        name="order_status"
                                        value={formData.order_status}
                                        onChange={handleInputChange}
                                        label="Order Status"
                                        required
                                    >
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                        <MenuItem value="cancelled">Cancelled</MenuItem>
                                        <MenuItem value="quotation">Quotation</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Order Notes (Optional)"
                                    name="order_notes"
                                    value={formData.order_notes}
                                    onChange={handleInputChange}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>
                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button
                                color="error"
                                onClick={handleCloseStatusUpdate}
                                variant="contained"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                            >
                                Update Status
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Make Payment Dialog */}
            <Dialog
                open={openPayment}
                onClose={handleClosePayment}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Make Payment - {selectedRecord?.order_number}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handlePayment} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <AlertTitle>Order Total</AlertTitle>
                                    <Typography variant="h6">
                                        {formatNumberWithComma(selectedRecord?.total_amount || 0)}
                                    </Typography>
                                </Alert>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Payment Method</InputLabel>
                                    <Select
                                        name="payment_method"
                                        value={paymentForm.payment_method}
                                        onChange={handlePaymentInputChange}
                                        label="Payment Method"
                                        required
                                    >
                                        {paymentModes.map((mode) => (
                                            <MenuItem key={mode.id} value={mode.id}>
                                                {mode.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Amount Paid"
                                    name="amount_paid"
                                    type="number"
                                    value={paymentForm.amount_paid}
                                    onChange={handlePaymentInputChange}
                                    required
                                    inputProps={{ 
                                        min: "0",
                                        max: selectedRecord?.total_amount || 0,
                                        step: "0.01"
                                    }}
                                    helperText={`Maximum: ${formatNumberWithComma(selectedRecord?.total_amount || 0)}`}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Transaction ID"
                                    name="transaction_id"
                                    value={paymentForm.transaction_id}
                                    onChange={handlePaymentInputChange}
                                    placeholder="e.g., MTN-123456, Bank Ref #"
                                    required={paymentForm.payment_method !== 'cash'}
                                    helperText={paymentForm.payment_method === 'cash' ? 'Optional for cash payments' : 'Required'}
                                />
                            </Grid>
                            <br/>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Payment Notes"
                                    name="payment_notes"
                                    value={paymentForm.payment_notes}
                                    onChange={handlePaymentInputChange}
                                    multiline
                                    rows={3}
                                    placeholder="Add any payment details, references, or notes..."
                                />
                            </Grid>
                        </Box>
                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button
                                color="error"
                                onClick={handleClosePayment}
                                variant="contained"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                            >
                                Process Payment
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={handleCloseDelete}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Order</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this order?
                    </Typography>
                    {selectedRecord && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {selectedRecord.order_number}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Customer: {selectedRecord.customer_id}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Date: {formatCreatedAtDate(selectedRecord.created_at)}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                                Total Amount: {formatNumberWithComma(selectedRecord.total_amount)}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Items: {selectedRecord.club_order_items?.length || 0}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleCloseDelete}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="primary"
                        variant="contained"
                    >
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Print Receipt Dialog */}
            <Dialog
                open={isPrintDialogOpen}
                onClose={handleClosePrintDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Print Receipt - {printReceiptData?.order_number}</b>
                </DialogTitle>
                <DialogContent>
                    {printReceiptData && (
                        <PrintOrderReceipt
                            orderData={printReceiptData}
                            servantName={getCategoriesNameById(printReceiptData.servant_id, referenceData.employeeNames)}
                            onPrintComplete={handleClosePrintDialog}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleClosePrintDialog}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={() => {
                            // Trigger print
                            window.print();
                        }}
                        color="primary"
                        variant="contained"
                        startIcon={<Print />}
                    >
                        Print Now
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ClubOrderList;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});